// FamilyRewards — Jul 2026 (Randy). One component, two modes:
//   • OWNER (parent, rendered in the calendar's Household area): manage the
//     reward store (create/edit/pause/delete), Browse-suggestions gallery,
//     see redemption history + "mark delivered", and the family leaderboard.
//   • MEMBER (email family-kid, rendered in the Fan-Portal Family view): their
//     points balance, the reward shop with claim buttons, their redemption
//     history, and the leaderboard.
//
// Feb 2026 — added `panel` prop for the owner side, letting the AI Calendar
// render the Reward store and Family leaderboard as SEPARATE collapsible
// sub-buttons instead of stacked on the same panel.
//   panel = "full"        → store + leaderboard (default, back-compat).
//   panel = "store"       → reward store + approved-but-not-delivered + history.
//   panel = "leaderboard" → just the family leaderboard.
// Member view ignores this prop (always full — shop + points + leaderboard).
//
// Also Feb 2026 — the reward creator/editor now uses a proper emoji picker
// (childish row + teen row) instead of a freeform text input.
import { useEffect, useState } from "react";
import { Gift, Sparkle, Trash, PencilSimple, Plus, X, Spinner, Trophy, Check, Pause, Play, Percent } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import MasterDashboardWalletCard from "@/components/MasterDashboardWalletCard";
import MasterEmojiGeneratorModal from "@/components/MasterEmojiGeneratorModal";
import { useThemeLabel } from "@/lib/CalendarThemeProvider";
import * as T from "@/lib/themeManifest";

// Feb 2026 — reward emoji palette. Two clearly labelled rows so parents can
// pick something age-appropriate; the freeform input under the palette is
// kept for anyone who wants a totally custom glyph.
const REWARD_EMOJIS_CHILDISH = [
  "🎁", "🍦", "🍭", "🍪", "🧸", "🎈", "🖍️", "📚", "🎨", "⚽",
  "🚲", "🐶", "🐱", "🦖", "🌈", "⭐", "🏰", "🎠",
];
const REWARD_EMOJIS_TEEN = [
  "🎧", "🎮", "🎬", "🛹", "🏀", "🎸", "📱", "💻", "🕶️", "🧢",
  "👟", "🍕", "🍔", "☕", "🥤", "🏆", "💰", "🔥", "💯", "✨",
];

export default function FamilyRewards({ siloId, panel = "full", targetMemberId = null }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Feb 2026 (Randy) — leaderboard cadence/reset settings (owner-only).
  const [leaderSettings, setLeaderSettings] = useState({ cadence: "monthly", last_reset_at: null });
  const [showLeaderSettings, setShowLeaderSettings] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/calendar/${siloId}/rewards`);
      setData(r.data);
    } catch { /* silent */ } finally { setLoading(false); }
  };
  const loadLeaderSettings = async () => {
    try {
      const r = await api.get(`/calendar/${siloId}/leaderboard-settings`);
      setLeaderSettings(r.data || { cadence: "monthly", last_reset_at: null });
    } catch { /* */ }
  };
  useEffect(() => { load(); loadLeaderSettings(); /* eslint-disable-next-line */ }, [siloId]);
  const saveLeaderSettings = async (payload) => {
    try {
      const r = await api.post(`/calendar/${siloId}/leaderboard-settings`, payload);
      setLeaderSettings({ cadence: r.data.cadence, last_reset_at: r.data.last_reset_at });
      toast.success(payload.reset_now ? "Leaderboard reset." : "Leaderboard cadence saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
  };

  if (loading) {
    return <div className="text-xs text-[#6B6A68] flex items-center gap-1.5 py-3" data-testid="family-rewards-loading"><Spinner size={12} className="animate-spin" /> Loading rewards…</div>;
  }
  if (!data) return null;
  return data.is_owner
    ? <OwnerRewards siloId={siloId} data={data} reload={load} panel={panel}
        leaderSettings={leaderSettings} showLeaderSettings={showLeaderSettings}
        setShowLeaderSettings={setShowLeaderSettings} saveLeaderSettings={saveLeaderSettings} />
    : <MemberRewards siloId={siloId} data={data} reload={load} targetMemberId={targetMemberId} />;
}

function Leaderboard({ rows, embedded = false, leaderSettings, showLeaderSettings, setShowLeaderSettings, saveLeaderSettings }) {
  if (!rows || rows.length === 0) {
    return embedded ? (
      <div className="text-xs text-[#6B6A68] italic py-2" data-testid="family-leaderboard-empty">
        No points earned yet — leaderboard fills in once chores start getting done.
      </div>
    ) : null;
  }
  const medal = ["🥇", "🥈", "🥉"];
  const wrapCls = embedded
    ? "space-y-1"
    : "mt-4 bg-[#FBF7EC] border border-[#EAD9AE] rounded-lg p-3";
  return (
    <div className={wrapCls} data-testid="family-leaderboard">
      {!embedded && (
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A6A24] mb-2 flex items-center gap-1.5">
          <Trophy size={12} weight="duotone" /> Family leaderboard
        </div>
      )}
      {/* Feb 2026 (Randy) — cadence/reset settings (owner-side). */}
      {saveLeaderSettings && (
        <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">
          <span>Cadence:</span>
          <select value={leaderSettings?.cadence || "monthly"}
            onChange={(e) => saveLeaderSettings({ cadence: e.target.value })}
            data-testid="leaderboard-cadence"
            className="px-2 py-1 border hairline rounded bg-white text-[10px]">
            <option value="weekly">Weekly</option>
            <option value="biweekly">Biweekly</option>
            <option value="monthly">Monthly</option>
          </select>
          <button onClick={() => saveLeaderSettings({ reset_now: true })}
            data-testid="leaderboard-reset-now"
            className="text-[10px] uppercase tracking-[0.14em] px-2 py-1 rounded border hairline hover:border-[#C16E5A] hover:text-[#C16E5A]">
            Reset now
          </button>
        </div>
      )}
      <div className="space-y-1">
        {rows.map((m, i) => (
          <div key={m.member_id} className="flex items-center gap-2 text-sm" data-testid={`leaderboard-row-${m.member_id}`}>
            <span className="w-5 text-center">{medal[i] || `${i + 1}.`}</span>
            <span className="text-lg leading-none">{m.emoji}</span>
            <span className="flex-1 truncate text-[#1C1B1A]">{m.name}</span>
            {m.best_streak > 0 && <span className="text-[11px] text-[#C16E5A]">🔥{m.best_streak}</span>}
            <span className="text-xs font-medium text-[#8A6A24] tabular-nums">{m.points} pts</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Feb 2026 — the picker used inside the reward editor. Grid of two labelled
// rows plus a freeform fallback input so any glyph is still typeable.
//
// pt89T (Feb 20, 2026, Randy) — the currently-selected emoji is now shown
// as a large tappable SQUARE at the top that opens the Master Emoji
// Generator. The two hardcoded palettes stay below as quick-pick fallback.
function RewardEmojiPicker({ value, onChange, siloId }) {
  const [megOpen, setMegOpen] = useState(false);
  return (
    <div className="space-y-2" data-testid="reward-emoji-picker">
      {siloId && (
        <div className="flex items-center gap-3">
          <button type="button" onClick={() => setMegOpen(true)}
            data-testid="reward-emoji-square"
            title="Tap to open the Master Emoji Generator (AI batch + silo library + nudge)"
            className="w-14 h-14 rounded-xl bg-white border-2 border-dashed border-[#C19A4A]/50 hover:border-[#C19A4A] hover:bg-[#F8F1E2] flex items-center justify-center text-3xl leading-none shadow-inner transition-colors">
            {value || "🎁"}
          </button>
          <div className="text-[10px] text-[#6B6A68] leading-tight">
            <div className="uppercase tracking-[0.14em]">Current icon</div>
            <div className="italic mt-0.5">Tap the square to open the AI generator, or pick a quick preset below.</div>
          </div>
        </div>
      )}
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">Kid-friendly</div>
        <div className="flex flex-wrap gap-1" data-testid="reward-emoji-picker-childish">
          {REWARD_EMOJIS_CHILDISH.map((e) => (
            <button key={e} type="button" onClick={() => onChange(e)}
              data-testid={`reward-emoji-child-${e}`}
              className={`w-8 h-8 rounded-md text-lg leading-none flex items-center justify-center hover:bg-[#F0F1EE] transition-colors ${value === e ? "bg-[#F8F1E2] ring-2 ring-[#C19A4A]" : "bg-white border hairline"}`}>
              {e}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68] mb-1">Teen</div>
        <div className="flex flex-wrap gap-1" data-testid="reward-emoji-picker-teen">
          {REWARD_EMOJIS_TEEN.map((e) => (
            <button key={e} type="button" onClick={() => onChange(e)}
              data-testid={`reward-emoji-teen-${e}`}
              className={`w-8 h-8 rounded-md text-lg leading-none flex items-center justify-center hover:bg-[#F0F1EE] transition-colors ${value === e ? "bg-[#F8F1E2] ring-2 ring-[#C19A4A]" : "bg-white border hairline"}`}>
              {e}
            </button>
          ))}
        </div>
      </div>
      <label className="block">
        <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Or type your own</span>
        <input value={value} onChange={(e) => onChange(e.target.value)} maxLength={4}
          data-testid="reward-emoji-input"
          className="mt-1 w-20 text-center text-xl px-2 py-1.5 border hairline rounded-md bg-[#FDFCF8]" />
      </label>
      {siloId && (
        <MasterEmojiGeneratorModal
          siloId={siloId}
          open={megOpen}
          onClose={() => setMegOpen(false)}
          onPick={(e) => onChange(e)}
          preserveParent={true}
          title="Master Emoji Generator"
        />
      )}
    </div>
  );
}

// ─────────────── Owner: manage the store ───────────────
// Feb 2026 (Randy) — persistent gallery memory. Anything the parent X'd
// (dismissed) stays gone across page reloads; anything the AI generated
// via "Suggest more" is remembered so it doesn't vanish on next open.
// Keys are scoped per silo. Normalized name (lowercased + whitespace
// collapsed) is used as the dedupe/dismissal key so cosmetic edits don't
// re-add a "duplicate".
const REW_KEY_NORM = (s) => (s || "").toLowerCase().split(/\s+/).join(" ");
const REW_DISMISS_KEY = (siloId) => `mysilo_reward_dismiss_${siloId}`;
const REW_GENERATED_KEY = (siloId) => `mysilo_reward_gen_${siloId}`;
const rewReadJson = (key, fallback) => {
  try { const v = JSON.parse(localStorage.getItem(key) || "null"); return v == null ? fallback : v; }
  catch { return fallback; }
};
const rewWriteJson = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch { /* */ } };

function OwnerRewards({ siloId, data, reload, panel = "full", leaderSettings, showLeaderSettings, setShowLeaderSettings, saveLeaderSettings }) {
  // Jun 2026 (Randy) — Theme QA dynamic-word sweep. Hooks live at the very
  // top, before the leaderboard-only early return.
  const tRewardStore = useThemeLabel(T.THEME_KEY_HOUSEHOLD_REWARD_STORE);
  const tStoreIntro  = useThemeLabel(T.THEME_KEY_REWARDS_STORE_INTRO);
  const [editor, setEditor] = useState(null);   // {id?, name, emoji, cost_points}
  const [gallery, setGallery] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [busy, setBusy] = useState(false);
  const [suggestBusy, setSuggestBusy] = useState(false);
  // Persistent memory (see notes above).
  const [dismissed, setDismissed] = useState(() => new Set(rewReadJson(REW_DISMISS_KEY(siloId), [])));
  const [genPersisted, setGenPersisted] = useState(() => rewReadJson(REW_GENERATED_KEY(siloId), []));
  // Feb 2026 (Randy) — reward-ideas gallery upgrades mirroring the AI
  // add-chore gallery: multi-select highlight, inline edit-in-stride,
  // bulk % cost reduction, and a bottom commit bar that promotes the
  // highlighted ideas into the official reward store.
  const [selectedSug, setSelectedSug] = useState(new Set());
  const [editingSug, setEditingSug] = useState(null);
  const [reducePct, setReducePct] = useState("10");
  const [commitBusy, setCommitBusy] = useState(false);
  // Jun 2026 (Randy) — reward-idea Nudge, mirroring the chore gallery. Blank
  // ⇒ household-context driven; non-blank ⇒ biases every "Suggest more" batch
  // toward the typed theme/arena.
  const [nudge, setNudge] = useState("");
  // (reward-store list chevron removed Jun 2026 — redundant with the AI
  // Calendar "Reward store" sub-button's own collapse chevron.)

  const openGallery = async () => {
    setGallery(true);
    // Rebuild suggestions from: staples ∪ persisted AI-generated, minus
    // dismissed. Fetch staples once (they're static, 15 items).
    try {
      const r = await api.get(`/calendar/reward-suggestions`);
      const staples = r.data.suggestions || [];
      // Guard against a stale dismissed row also matching a store item —
      // we simply don't re-add anything already in the reward store.
      const inStore = new Set((data.rewards || []).map((r_) => REW_KEY_NORM(r_.name)));
      const combined = [...genPersisted, ...staples];
      const merged = [];
      const seen = new Set();
      for (const s of combined) {
        const k = REW_KEY_NORM(s.name);
        if (!k || seen.has(k) || dismissed.has(k) || inStore.has(k)) continue;
        seen.add(k);
        merged.push(s);
      }
      setSuggestions(merged);
    } catch { toast.error("Couldn't load suggestions."); }
  };
  const removeSuggestion = (id, name) => {
    setSuggestions((cur) => cur.filter((s) => s.id !== id));
    const k = REW_KEY_NORM(name);
    if (!k) return;
    setDismissed((cur) => {
      const next = new Set(cur); next.add(k);
      rewWriteJson(REW_DISMISS_KEY(siloId), Array.from(next));
      return next;
    });
    // If it was one of the AI-generated persisted rows, drop it there too
    // so we don't keep it "alive" on the next reload just to filter it out.
    setGenPersisted((cur) => {
      const trimmed = cur.filter((s) => REW_KEY_NORM(s.name) !== k);
      if (trimmed.length !== cur.length) rewWriteJson(REW_GENERATED_KEY(siloId), trimmed);
      return trimmed;
    });
  };
  const suggestMore = async () => {
    setSuggestBusy(true);
    try {
      const already = suggestions.map((s) => (s.name || "").trim()).filter(Boolean);
      const dismissedNames = Array.from(dismissed);
      // Feed the LLM both what's on screen AND what's been dismissed so a
      // parent's rejected ideas never come back through the AI door.
      const r = await api.post(`/calendar/${siloId}/reward-suggestions/generate`, {
        already_seen: [...already, ...dismissedNames],
        nudge: nudge.trim() || null,
      });
      const seen = new Set([...already, ...dismissedNames].map(REW_KEY_NORM));
      const fresh = (r.data.suggestions || []).filter((s) => {
        const key = REW_KEY_NORM(s.name);
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      if (fresh.length === 0) {
        toast("No new reward ideas this time — try again after adding more chores.", { id: "rewsugg-none" });
      }
      setSuggestions((cur) => [...fresh, ...cur]);
      // Persist the AI-generated ideas so they come back on next open.
      setGenPersisted((cur) => {
        const next = [...fresh, ...cur];
        rewWriteJson(REW_GENERATED_KEY(siloId), next);
        return next;
      });
    } catch (e) {
      if (e.response?.status === 402 && e.response?.data?.guard_code === "LLM_GUARD") return;
      toast.error(e.response?.data?.detail || "Couldn't generate reward ideas.");
    } finally { setSuggestBusy(false); }
  };
  const toggleSelectSug = (s) => {
    setSelectedSug((cur) => {
      const n = new Set(cur);
      if (n.has(s.id)) n.delete(s.id); else n.add(s.id);
      return n;
    });
  };
  const clearSelectedSug = () => setSelectedSug(new Set());

  const applyReducePct = () => {
    const pct = Math.max(0, Math.min(100, Number(reducePct) || 0));
    if (pct <= 0) { toast.error("Pick a percentage first."); return; }
    if (selectedSug.size === 0) { toast.error("Highlight at least one reward idea first."); return; }
    const reduce = (c) => Math.max(1, Math.round((Number(c) || 0) * (1 - pct / 100)));
    setSuggestions((cur) => cur.map((s) => selectedSug.has(s.id) ? { ...s, cost_points: reduce(s.cost_points) } : s));
    setGenPersisted((cur) => {
      const next = cur.map((s) => selectedSug.has(s.id) ? { ...s, cost_points: reduce(s.cost_points) } : s);
      rewWriteJson(REW_GENERATED_KEY(siloId), next);
      return next;
    });
    toast.success(`Reduced ${selectedSug.size} idea${selectedSug.size === 1 ? "" : "s"} by ${pct}%.`);
  };

  const saveEditedSug = (updated) => {
    setSuggestions((cur) => cur.map((s) => s.id === updated.id ? { ...s, ...updated } : s));
    setGenPersisted((cur) => {
      const next = cur.map((s) => s.id === updated.id ? { ...s, ...updated } : s);
      rewWriteJson(REW_GENERATED_KEY(siloId), next);
      return next;
    });
    setEditingSug(null);
    toast.success("Idea updated.");
  };

  const commitSelectedSug = async () => {
    const items = suggestions.filter((s) => selectedSug.has(s.id));
    if (items.length === 0) return;
    setCommitBusy(true);
    try {
      for (const s of items) {
        await api.post(`/calendar/${siloId}/rewards`, {
          name: s.name, emoji: s.emoji || "🎁",
          cost_points: Math.max(1, Number(s.cost_points) || 1),
        });
      }
      const norms = new Set(items.map((s) => REW_KEY_NORM(s.name)));
      setSuggestions((cur) => cur.filter((s) => !selectedSug.has(s.id)));
      setDismissed((cur) => {
        const n = new Set(cur); items.forEach((s) => n.add(REW_KEY_NORM(s.name)));
        rewWriteJson(REW_DISMISS_KEY(siloId), Array.from(n)); return n;
      });
      setGenPersisted((cur) => {
        const next = cur.filter((s) => !norms.has(REW_KEY_NORM(s.name)));
        rewWriteJson(REW_GENERATED_KEY(siloId), next); return next;
      });
      clearSelectedSug();
      setGallery(false);
      reload();
      toast.success(`Added ${items.length} reward${items.length === 1 ? "" : "s"} to the store.`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't add rewards.");
    } finally { setCommitBusy(false); }
  };

  const save = async () => {
    if (!editor?.name?.trim()) { toast.error("Give the reward a name."); return; }
    setBusy(true);
    try {
      if (editor.id) {
        await api.patch(`/calendar/${siloId}/rewards/${editor.id}`, {
          name: editor.name, emoji: editor.emoji, cost_points: Number(editor.cost_points) || 1,
        });
      } else {
        await api.post(`/calendar/${siloId}/rewards`, {
          name: editor.name, emoji: editor.emoji || "🎁", cost_points: Number(editor.cost_points) || 1,
        });
      }
      setEditor(null); reload();
      toast.success("Reward saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setBusy(false); }
  };
  const togglePause = async (r) => {
    try { await api.patch(`/calendar/${siloId}/rewards/${r.id}`, { active: !r.active }); reload(); }
    catch { toast.error("Couldn't update."); }
  };
  const del = async (r) => {
    if (!window.confirm(`Delete "${r.name}"?`)) return;
    try { await api.delete(`/calendar/${siloId}/rewards/${r.id}`); reload(); toast("Reward deleted."); }
    catch { toast.error("Delete failed."); }
  };
  const deliver = async (red) => {
    try { await api.post(`/calendar/${siloId}/redemptions/${red.id}/deliver`); reload(); toast.success("Marked delivered."); }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't update."); }
  };

  const approved = (data.redemptions || []).filter((r) => r.status === "approved");
  const history = (data.redemptions || []).filter((r) => r.status === "delivered" || r.status === "declined").slice(0, 8);

  // Feb 2026 — leaderboard-only mode used by the AI Calendar's separate
  // "Family leaderboard" sub-button.
  if (panel === "leaderboard") {
    return <Leaderboard rows={data.leaderboard} embedded
      leaderSettings={leaderSettings} showLeaderSettings={showLeaderSettings}
      setShowLeaderSettings={setShowLeaderSettings} saveLeaderSettings={saveLeaderSettings} />;
  }

  // The rest of the owner UI (store, approved queue, history). This body is
  // used for panel="full" (legacy stacked view) and panel="store" (AI
  // Calendar's separate Reward store sub-button — no leaderboard here).
  return (
    <div className="mt-3" data-testid="owner-rewards">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex items-center gap-1.5" data-testid="reward-store-header">
          <Gift size={14} weight="duotone" className="text-[#C16E5A]" />
          <span className="text-sm font-medium text-[#1C1B1A]">{tRewardStore}</span>
          <span className="text-[10px] text-[#8A6A24] font-medium tabular-nums">({(data.rewards || []).length})</span>
        </div>
        <span className="flex-1" />
        <button onClick={openGallery} data-testid="reward-gallery-btn"
          className="text-[10px] px-2 py-1 rounded border hairline hover:border-[#4A5D4E] flex items-center gap-1">
          <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" /> Browse suggestions
        </button>
        <button onClick={() => setEditor({ name: "", emoji: "🎁", cost_points: 50 })} data-testid="reward-new-btn"
          className="text-[10px] px-2 py-1 rounded bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] flex items-center gap-1">
          <Plus size={11} weight="bold" /> New reward
        </button>
      </div>
      <p className="text-[11px] text-[#6B6A68] mb-2 leading-relaxed">
        {tStoreIntro}
      </p>

      <div className="space-y-1.5" data-testid="reward-store-list">
        {(data.rewards || []).map((r) => (
          <div key={r.id} className={`flex items-center gap-2 bg-white border hairline rounded px-2.5 py-1.5 text-sm ${!r.active ? "opacity-55" : ""}`} data-testid={`reward-row-${r.id}`}>
            <span className="text-lg leading-none">{r.emoji}</span>
            <span className="flex-1 truncate text-[#1C1B1A]">{r.name}</span>
            <span className="text-xs text-[#8A6A24] font-medium tabular-nums shrink-0">{r.cost_points} pts</span>
            <button onClick={() => togglePause(r)} title={r.active ? "Pause" : "Resume"} data-testid={`reward-pause-${r.id}`} className="text-[#6B6A68] hover:text-[#4A5D4E]">
              {r.active ? <Pause size={13} /> : <Play size={13} />}
            </button>
            <button onClick={() => setEditor({ id: r.id, name: r.name, emoji: r.emoji, cost_points: r.cost_points })} data-testid={`reward-edit-${r.id}`} className="text-[#6B6A68] hover:text-[#4A5D4E]"><PencilSimple size={13} /></button>
            <button onClick={() => del(r)} data-testid={`reward-delete-${r.id}`} className="text-[#6B6A68] hover:text-[#C16E5A]"><Trash size={13} /></button>
          </div>
        ))}
        {(data.rewards || []).length === 0 && <div className="text-xs text-[#6B6A68] italic">No rewards yet — add one or Browse suggestions.</div>}
      </div>

      {/* approved-but-not-delivered → nudge to mark delivered */}
      {approved.length > 0 && (
        <div className="mt-3">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] mb-1">Approved — give it & mark done</div>
          <div className="space-y-1">
            {approved.map((red) => (
              <div key={red.id} className="flex items-center gap-2 bg-[#EAF3EC] border border-[#B9D4BE] rounded px-2.5 py-1.5 text-sm" data-testid={`approved-redemption-${red.id}`}>
                <span>{red.reward_emoji}</span>
                <span className="flex-1 truncate text-[#1C1B1A]">{red.reward_name} <span className="text-[11px] text-[#6B6A68]">· {red.redeemer_name}</span></span>
                <button onClick={() => deliver(red)} data-testid={`deliver-redemption-${red.id}`}
                  className="text-[10px] px-2 py-1 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] flex items-center gap-1">
                  <Check size={11} weight="bold" /> Mark delivered
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {history.length > 0 && (
        <details className="mt-3" data-testid="redemption-history">
          <summary className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] cursor-pointer">Redemption history</summary>
          <div className="mt-1 space-y-0.5">
            {history.map((red) => (
              <div key={red.id} className="text-[11px] text-[#6B6A68] flex items-center gap-1.5">
                <span>{red.reward_emoji}</span>
                <span className="flex-1 truncate">{red.reward_name} · {red.redeemer_name}</span>
                <span className={red.status === "delivered" ? "text-[#2F6B3A]" : "text-[#C16E5A]"}>{red.status}</span>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Legacy stacked view keeps the leaderboard here; the split panels
          render it as its own sub-button, so we skip it in panel="store". */}
      {panel === "full" && <Leaderboard rows={data.leaderboard}
        leaderSettings={leaderSettings} showLeaderSettings={showLeaderSettings}
        setShowLeaderSettings={setShowLeaderSettings} saveLeaderSettings={saveLeaderSettings} />}

      {gallery && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setGallery(false)}>
          <div className="bg-[#FDFCF8] rounded-2xl w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="reward-suggestions-gallery">
            <div className="px-4 py-3 border-b hairline flex items-center justify-between bg-[#FDFCF8] shrink-0">
              <h2 className="font-serif-display text-lg flex items-center gap-2"><Sparkle size={16} weight="duotone" className="text-[#C19A4A]" /> Reward ideas</h2>
              <div className="flex items-center gap-2">
                <button onClick={suggestMore} disabled={suggestBusy} data-testid="reward-suggest-more"
                  title="AI suggests fresh, age-appropriate rewards based on your kids' chore list (uses your BYOK key)"
                  className="text-[10px] px-2.5 py-1.5 rounded border hairline hover:border-[#4A5D4E] disabled:opacity-50 flex items-center gap-1">
                  {suggestBusy ? <Spinner size={11} className="animate-spin" /> : <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" />} Suggest more (uses your key)
                </button>
                <button onClick={() => setGallery(false)} className="p-1 rounded hover:bg-[#F0F1EE]" data-testid="reward-gallery-close"><X size={16} /></button>
              </div>
            </div>

            {/* Jun 2026 (Randy) — reward-idea Nudge, mirroring the chore
                gallery. Blank ⇒ household-context driven; non-blank ⇒ biases
                every "Suggest more" batch toward the typed theme/arena. */}
            <div className="px-4 pt-3 pb-2 border-b hairline bg-[#FBFAF6] shrink-0">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-[#6B6A68] flex items-center gap-1.5">
                  <Sparkle size={11} weight="duotone" className="text-[#C19A4A]" /> Nudge
                  <span className="normal-case tracking-normal text-[#8A8A88] italic ml-auto">optional — biases every &ldquo;Suggest more&rdquo;</span>
                </span>
                <input type="text" value={nudge}
                  onChange={(e) => setNudge(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !suggestBusy) { e.preventDefault(); suggestMore(); } }}
                  data-testid="reward-suggest-nudge"
                  placeholder="e.g. outdoors, screen-free, soccer, art supplies, movie night"
                  maxLength={120}
                  className="mt-1 w-full px-3 py-2 border hairline rounded-md bg-white text-sm focus:outline-none focus:border-[#4A5D4E]" />
              </label>
            </div>

            {/* Feb 2026 (Randy) — bulk % cost reduction for highlighted ideas */}
            <div className="px-4 py-2 border-b hairline bg-[#FBF8F0] shrink-0 flex items-center gap-2" data-testid="reward-reduce-bar">
              <span className="text-[11px] text-[#6B6A68] whitespace-nowrap">Reduce highlighted by</span>
              <div className="relative">
                <input value={reducePct}
                  onChange={(e) => setReducePct(e.target.value.replace(/[^0-9]/g, "").slice(0, 3))}
                  inputMode="numeric" data-testid="reward-reduce-input"
                  className="w-14 pl-2 pr-5 py-1 text-sm rounded border hairline bg-white text-center tabular-nums" />
                <Percent size={11} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-[#8A6A24]" />
              </div>
              <select value="" onChange={(e) => { if (e.target.value) setReducePct(e.target.value); }}
                data-testid="reward-reduce-preset" title="Pick a preset percentage"
                className="text-[11px] py-1.5 rounded border hairline bg-white text-[#6B6A68]">
                <option value="">%…</option>
                {["10", "20", "30", "40", "50"].map((p) => <option key={p} value={p}>{p}%</option>)}
              </select>
              <span className="flex-1" />
              <button onClick={applyReducePct} disabled={selectedSug.size === 0} data-testid="reward-reduce-submit"
                className="text-[11px] px-3 py-1.5 rounded bg-[#4A5D4E] text-white hover:bg-[#3A4A3E] disabled:opacity-40 disabled:cursor-not-allowed">
                Submit
              </button>
            </div>

            <div className="p-3 space-y-1.5 overflow-y-auto flex-1">
              {suggestions.map((s) => {
                const sel = selectedSug.has(s.id);
                return (
                  <div key={s.id} className="relative">
                    <button onClick={() => toggleSelectSug(s)} data-testid={`reward-suggestion-${s.id}`} aria-pressed={sel}
                      className={`w-full text-left flex items-center gap-2 p-2.5 pr-16 rounded-md border-2 transition-colors ${sel ? "border-[#4A5D4E] bg-[#EAF0EA]" : "border-transparent bg-white hairline hover:border-[#4A5D4E]/40"}`}>
                      <span className={`w-4 h-4 rounded-full border shrink-0 flex items-center justify-center ${sel ? "bg-[#4A5D4E] border-[#4A5D4E]" : "border-[#C9C7C0]"}`}>
                        {sel && <Check size={10} weight="bold" className="text-white" />}
                      </span>
                      <span className="text-xl">{s.emoji}</span>
                      <span className="flex-1 text-sm text-[#1C1B1A]">{s.name}</span>
                      <span className="text-xs text-[#8A6A24] font-medium tabular-nums">{s.cost_points} pts</span>
                    </button>
                    <div className="absolute top-1/2 -translate-y-1/2 right-1.5 flex items-center gap-0.5">
                      <button onClick={(e) => { e.stopPropagation(); setEditingSug(s); }}
                        data-testid={`reward-suggestion-edit-${s.id}`} title="Edit this idea"
                        className="p-1 rounded-full text-[#6B6A68] hover:text-[#4A5D4E] hover:bg-white transition-colors">
                        <PencilSimple size={13} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); removeSuggestion(s.id, s.name); }}
                        data-testid={`reward-suggestion-remove-${s.id}`}
                        title="Remove this idea (Suggest more won't repeat it in this session)"
                        className="p-1 rounded-full text-[#B5B3AE] hover:text-[#C16E5A] hover:bg-white transition-colors">
                        <X size={13} weight="bold" />
                      </button>
                    </div>
                  </div>
                );
              })}
              {suggestions.length === 0 && !suggestBusy && (
                <div className="text-xs text-[#6B6A68] italic p-2">
                  No ideas left — tap "Suggest more" for AI-generated ones matched to your kids' chores.
                </div>
              )}
            </div>

            {selectedSug.size > 0 && (
              <div className="px-4 py-3 border-t hairline bg-[#FDFCF8] shrink-0 flex items-center gap-2 cta-safe" data-testid="reward-commit-bar">
                <span className="text-[11px] text-[#6B6A68]">{selectedSug.size} highlighted</span>
                <button onClick={clearSelectedSug} data-testid="reward-commit-clear"
                  className="text-[11px] px-2.5 py-1.5 rounded border hairline hover:border-[#4A5D4E]">Clear</button>
                <span className="flex-1" />
                <button onClick={commitSelectedSug} disabled={commitBusy} data-testid="reward-commit-btn"
                  className="text-[12px] px-4 py-2 rounded-full bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
                  {commitBusy ? <Spinner size={12} className="animate-spin" /> : <Plus size={12} weight="bold" />}
                  Add {selectedSug.size} to rewards
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {editingSug && (
        <InlineRewardEditor sug={editingSug} onSave={saveEditedSug} onCancel={() => setEditingSug(null)} />
      )}

      {editor && (
        <div className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4" onClick={() => setEditor(null)}>
          <div className="bg-white rounded-2xl w-full max-w-sm p-5 max-h-[90dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="reward-editor">
            <h2 className="font-serif-display text-lg mb-3">{editor.id ? "Edit reward" : "New reward"}</h2>
            <label className="block">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Reward name</span>
              <input value={editor.name} onChange={(e) => setEditor({ ...editor, name: e.target.value })}
                placeholder="e.g., 30 min extra screen time" data-testid="reward-name-input" autoFocus
                className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            </label>
            <label className="block mt-3">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Point cost</span>
              <input type="number" min={1} value={editor.cost_points} onChange={(e) => setEditor({ ...editor, cost_points: e.target.value })}
                data-testid="reward-cost-input"
                className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            </label>
            <div className="mt-3">
              <span className="text-[10px] uppercase tracking-[0.14em] text-[#6B6A68]">Icon</span>
              <div className="mt-1">
                <RewardEmojiPicker value={editor.emoji} onChange={(v) => setEditor({ ...editor, emoji: v })} siloId={siloId} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setEditor(null)} className="text-xs px-3 py-2 rounded text-[#6B6A68]">Cancel</button>
              <button onClick={save} disabled={busy} data-testid="reward-save-btn"
                className="text-xs px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
                {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────── Inline reward-idea editor (Feb 2026, Randy) ───────────────
// Edit a suggestion in stride from the Reward ideas gallery. Saving updates
// the idea in place; cancelling / backing out just returns to the gallery
// WITHOUT selecting it (mirrors the AI add-chore edit flow).
function InlineRewardEditor({ sug, onSave, onCancel }) {
  const [name, setName] = useState(sug.name || "");
  const [emoji, setEmoji] = useState(sug.emoji || "🎁");
  const [cost, setCost] = useState(String(sug.cost_points ?? 10));
  return (
    <div className="fixed inset-0 z-[80] bg-black/50 flex items-center justify-center p-4" onClick={onCancel} data-testid="reward-suggestion-editor">
      <div className="modal-safe bg-[#FDFCF8] rounded-2xl w-full max-w-sm p-4" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-serif-display text-base mb-3 flex items-center gap-2"><PencilSimple size={15} weight="duotone" className="text-[#4A5D4E]" /> Edit idea</h3>
        <div className="flex items-center gap-2 mb-3">
          <input value={emoji} onChange={(e) => setEmoji(e.target.value)} maxLength={4}
            data-testid="reward-edit-emoji" aria-label="Emoji"
            className="w-14 text-center text-2xl py-2 rounded border hairline bg-white" />
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Reward name"
            data-testid="reward-edit-name"
            className="flex-1 px-3 py-2 rounded border hairline bg-white text-sm" />
        </div>
        <label className="block text-[11px] text-[#6B6A68] mb-1">Point cost</label>
        <input value={cost} onChange={(e) => setCost(e.target.value.replace(/[^0-9]/g, ""))} inputMode="numeric"
          data-testid="reward-edit-cost"
          className="w-full px-3 py-2 rounded border hairline bg-white text-sm tabular-nums mb-4" />
        <div className="flex items-center gap-2 cta-safe">
          <button onClick={onCancel} data-testid="reward-edit-cancel"
            className="flex-1 py-2 rounded-full border hairline text-sm hover:border-[#4A5D4E]">Cancel</button>
          <button data-testid="reward-edit-save"
            onClick={() => {
              const nm = name.trim();
              if (!nm) { toast.error("Give the reward a name."); return; }
              onSave({ ...sug, name: nm, emoji: emoji.trim() || "🎁", cost_points: Math.max(1, Number(cost) || 1) });
            }}
            className="flex-1 py-2 rounded-full bg-[#1C1B1A] text-[#FDFCF8] text-sm hover:bg-[#3A4A3E]">Save</button>
        </div>
      </div>
    </div>
  );
}

// ─────────────── Member (email family-kid): the shop ───────────────
function MemberRewards({ siloId, data, reload, targetMemberId }) {
  const [busyId, setBusyId] = useState(null);
  // Feb 2026 (Randy) — Achieved-Rewards wallet: parent-approved tickets
  // still awaiting real-world use. Fetches its own list because the
  // regular /rewards endpoint filters `my_redemptions` by the logged-in
  // user. In targetMemberId mode (Master Dashboard), the wallet is for
  // that specific member.
  const [wallet, setWallet] = useState([]);
  const loadWallet = async () => {
    if (!targetMemberId) return;
    try {
      const r = await api.get(`/calendar/${siloId}/wallet/${encodeURIComponent(targetMemberId)}`);
      setWallet(r.data?.tickets || []);
    } catch { setWallet([]); }
  };
  useEffect(() => { loadWallet(); /* eslint-disable-next-line */ }, [siloId, targetMemberId]);
  const wipeWalletTicket = async (t) => {
    try {
      await api.post(`/calendar/${siloId}/wallet/${t.id}/wipe`);
      toast.success("Used ✓");
      loadWallet();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't wipe."); }
  };
  const claim = async (r) => {
    if (r.cost_points > (data.points_spendable || 0)) { toast.error("Not enough points yet — keep earning!"); return; }
    setBusyId(r.id);
    try {
      await api.post(`/calendar/${siloId}/rewards/${r.id}/redeem`);
      toast.success("Sent to a parent for approval! 🎉");
      reload();
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't claim that."); }
    finally { setBusyId(null); }
  };
  const pending = (data.my_redemptions || []).filter((x) => x.status === "pending");
  const recent = (data.my_redemptions || []).filter((x) => x.status !== "pending").slice(0, 5);

  return (
    <section className="bg-white border hairline rounded-lg p-5" data-testid="member-rewards">
      <div className="flex items-center gap-2 mb-3">
        <Gift size={16} weight="duotone" className="text-[#C16E5A]" />
        <h3 className="font-serif-display text-lg text-[#1C1B1A]">Rewards</h3>
        <span className="flex-1" />
        <span className="text-xs px-3 py-1.5 rounded-full bg-[#F8F1E2] border border-[#EAD9AE] text-[#8A6A24] font-medium" data-testid="member-points-chip">
          ⭐ {data.points_spendable} to spend
        </span>
      </div>
      {(data.points_spendable || 0) === 0 && (data.points_lifetime || 0) === 0 && (
        <p className="text-[11px] text-[#6B6A68] -mt-1 mb-3" data-testid="member-earn-hint">Finish chores on the calendar to earn points — 10 each!</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {(data.rewards || []).map((r) => {
          const afford = r.cost_points <= (data.points_spendable || 0);
          return (
            <button key={r.id} onClick={() => claim(r)} disabled={!afford || busyId === r.id}
              data-testid={`member-reward-${r.id}`}
              className={`flex items-center gap-2 text-left p-3 rounded-lg border-2 transition-colors ${afford ? "border-[#DCE5DD] hover:border-[#4A5D4E] bg-white" : "border-[#EDECE8] bg-[#FAFAF8] opacity-60 cursor-not-allowed"}`}>
              <span className="text-2xl">{r.emoji}</span>
              <span className="flex-1 min-w-0">
                <span className="block text-sm text-[#1C1B1A] truncate">{r.name}</span>
                <span className="text-[11px] text-[#8A6A24] font-medium">{r.cost_points} pts</span>
              </span>
              {busyId === r.id && <Spinner size={13} className="animate-spin" />}
            </button>
          );
        })}
        {(data.rewards || []).length === 0 && <div className="text-xs text-[#6B6A68] italic col-span-full">No rewards set up yet — ask a parent!</div>}
      </div>

      {pending.length > 0 && (
        <div className="mt-3 bg-[#FDF9EF] border border-[#EAD9AE] rounded-md p-2.5" data-testid="member-pending-redemptions">
          <div className="text-[10px] uppercase tracking-[0.18em] text-[#8A6A24] mb-1">⏳ Waiting for a parent's OK</div>
          {pending.map((x) => (
            <div key={x.id} className="text-sm text-[#1C1B1A]">• {x.reward_emoji} {x.reward_name} ({x.cost_points} pts)</div>
          ))}
        </div>
      )}
      {recent.length > 0 && (
        <div className="mt-2 space-y-0.5">
          {recent.map((x) => (
            <div key={x.id} className="text-[11px] text-[#6B6A68] flex items-center gap-1.5">
              <span>{x.reward_emoji}</span><span className="flex-1 truncate">{x.reward_name}</span>
              <span className={x.status === "delivered" ? "text-[#2F6B3A]" : x.status === "approved" ? "text-[#4A5D4E]" : "text-[#C16E5A]"}>{x.status}</span>
            </div>
          ))}
        </div>
      )}

      {/* Achieved-rewards wallet — teenage/young-adult ticket cards */}
      {wallet.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.24em] text-[#6B6A68] mb-2 flex items-center gap-1.5">
            <Trophy size={12} weight="duotone" /> Achieved Rewards ({wallet.length})
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {wallet.map((t) => (
              <MasterDashboardWalletCard key={t.id} ticket={t}
                onWipe={wipeWalletTicket} compact />
            ))}
          </div>
        </div>
      )}

      <Leaderboard rows={data.leaderboard} />
    </section>
  );
}
